// Base API Client with cookie handling and error management

import type { ApiError } from './types/api'

// Vite exposes env vars prefixed with VITE_; fallback for dev
export const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (import.meta.env?.DEV ? 'http://localhost:3000' : 'https://api.binyumatv.com')

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public error?: ApiError
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError
    
    try {
      errorData = await response.json()
    } catch {
      // If response is not JSON, create a generic error
      errorData = {
        error: response.statusText || 'An error occurred'
      }
    }
    
    const message =
      errorData.error ||
      (errorData as { message?: string }).message ||
      response.statusText ||
      'An error occurred'
    throw new ApiClientError(message, response.status, errorData)
  }
  
  // Handle empty responses (e.g., 204 No Content)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {} as T
  }
  
  return response.json()
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
  
  // Build headers object
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Attach Bearer token so API Gateway / Cognito authorizer can authenticate requests
  if (typeof window !== 'undefined') {
    const token = sessionStorage.getItem('accessToken')
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  // Merge with existing headers if they're a plain object
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value
      })
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value
      })
    } else {
      Object.assign(headers, options.headers)
    }
  }
  
  // Remove Content-Type for FormData (browser will set it with boundary)
  if (options.body instanceof FormData) {
    delete headers['Content-Type']
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
    // Include credentials so the website can share auth cookies
    // with api.binyumatv.com like the existing frontend/app.
    credentials: 'include',
  })
  
  return handleResponse<T>(response)
}

export async function apiGet<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'GET',
  })
}

export async function apiPost<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
  })
}

export async function apiPut<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function apiDelete<T>(endpoint: string, options?: RequestInit): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'DELETE',
  })
}

